import type { OralExamSupervisorEntryResponse } from '@/common/api/models/oralExamSupervisorEntryResponse';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import type { OralExamSupervisorVisitResponseRung } from '@/common/api/models/oralExamSupervisorVisitResponseRung';

export function isVisitAboveObserving(rung: OralExamSupervisorVisitResponseRung): boolean {
  return rung === 'visible' || rung === 'speaking';
}

export function getSupervisorVisit(
  supervisors: OralExamSupervisorEntryResponse[] | undefined,
  lecturerId: string,
) {
  return supervisors?.find((entry) => entry.lecturerId === lecturerId)?.visit ?? null;
}

/** Mirror backend derive_supervisor_status for the viewing lecturer's wall tile. */
export function deriveSupervisorStatusForViewer(
  supervisors: OralExamSupervisorEntryResponse[] | undefined,
  bookingId: string,
  viewerLecturerId: string,
): typeof OralExamSupervisorStatus[keyof typeof OralExamSupervisorStatus] {
  const visit = getSupervisorVisit(supervisors, viewerLecturerId);
  if (!visit || visit.bookingId !== bookingId) {
    return OralExamSupervisorStatus.watching;
  }
  if (visit.rung === 'visible') return OralExamSupervisorStatus.here_with_you;
  if (visit.rung === 'speaking') return OralExamSupervisorStatus.engaged;
  return OralExamSupervisorStatus.watching;
}

export type ColleagueVisitFlag = {
  lecturerId: string;
  displayName: string;
  rung: OralExamSupervisorVisitResponseRung;
};

export function colleagueVisitsOnBooking(
  supervisors: OralExamSupervisorEntryResponse[] | undefined,
  bookingId: string,
  viewerLecturerId: string,
): ColleagueVisitFlag[] {
  if (!supervisors?.length) return [];
  const flags: ColleagueVisitFlag[] = [];
  supervisors.forEach((entry) => {
    if (entry.lecturerId === viewerLecturerId) return;
    const { visit } = entry;
    if (!visit || visit.bookingId !== bookingId) return;
    flags.push({
      lecturerId: entry.lecturerId,
      displayName: entry.displayName ?? entry.lecturerId,
      rung: visit.rung,
    });
  });
  return flags;
}

export function visitHolderAboveObserving(
  supervisors: OralExamSupervisorEntryResponse[] | undefined,
  bookingId: string,
  viewerLecturerId: string,
): { holderName: string; rung: 'visible' | 'speaking' } | null {
  const holder = (supervisors ?? []).find((entry) => {
    if (entry.lecturerId === viewerLecturerId) return false;
    const { visit } = entry;
    if (!visit || visit.bookingId !== bookingId) return false;
    return visit.rung === 'visible' || visit.rung === 'speaking';
  });
  if (!holder) return null;
  const { visit } = holder;
  if (!visit || (visit.rung !== 'visible' && visit.rung !== 'speaking')) return null;
  return {
    holderName: holder.displayName ?? holder.lecturerId,
    rung: visit.rung,
  };
}

export function otherConnectedSupervisorNames(
  supervisors: OralExamSupervisorEntryResponse[] | undefined,
  viewerLecturerId: string,
): string[] {
  return (supervisors ?? [])
    .filter((entry) => entry.lecturerId !== viewerLecturerId && entry.connected !== false)
    .map((entry) => entry.displayName ?? entry.lecturerId);
}

/** The student this lecturer is quietly zoomed on. The student still reads Watching. */
export function viewerQuietObserveBookingId(
  supervisors: OralExamSupervisorEntryResponse[] | undefined,
  viewerLecturerId: string | null | undefined,
): string | null {
  if (!viewerLecturerId) return null;
  const visit = getSupervisorVisit(supervisors, viewerLecturerId);
  if (!visit || visit.rung !== 'observing') return null;
  return visit.bookingId;
}

export function viewerActiveDropInBookingId(
  supervisors: OralExamSupervisorEntryResponse[] | undefined,
  viewerLecturerId: string,
): string | null {
  const visit = getSupervisorVisit(supervisors, viewerLecturerId);
  if (!visit || !isVisitAboveObserving(visit.rung)) return null;
  return visit.bookingId;
}
